import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import type { Custodian, CustodianType } from '../api';
import { labelForCustodianType } from '../../../shared/utils/roleLabels';
import { useCustodians } from '../hooks';

const BADGE_CLASS = 'bg-gray-200 text-gray-800';

interface Props {
  value: Custodian | null;
  onChange: (custodian: Custodian | null) => void;
  filterType?: CustodianType;
  placeholder?: string;
}

// Nota: cadastro de custodiário só pela tela dedicada /custodiarios.
// Este autocomplete é read-only — nada de "criar novo" inline.
export function CustodianAutocomplete({ value, onChange, filterType, placeholder }: Props) {
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), 300);
    return () => clearTimeout(t);
  }, [input]);

  const query = useCustodians({ type: filterType, search: debounced || undefined });

  const results = useMemo(() => query.data?.data ?? [], [query.data]);

  if (value) {
    return (
      <div className="flex items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${BADGE_CLASS}`}>
            {labelForCustodianType(value.type)}
          </span>
          <div className="min-w-0">
            <p className="text-sm text-gray-900 truncate">{value.name}</p>
            {value.email && <p className="text-xs text-gray-500 truncate">{value.email}</p>}
          </div>
        </div>
        <button type="button" onClick={() => onChange(null)} className="text-xs text-blue-600 hover:underline">
          Alterar
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={input}
          onChange={(e) => { setInput(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder ?? 'Buscar responsável por nome ou e-mail...'}
          className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      {open && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-64 overflow-y-auto">
          {query.isLoading && <p className="px-3 py-2 text-xs text-gray-500">Buscando...</p>}
          {!query.isLoading && results.length === 0 && (
            <p className="px-3 py-2 text-xs text-gray-500">
              Nenhum responsável encontrado{debounced ? ` para "${debounced}"` : ''}.
            </p>
          )}
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(c); setOpen(false); setInput(''); }}
              className="flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-blue-50"
            >
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${BADGE_CLASS}`}>
                {labelForCustodianType(c.type)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-900 truncate">{c.name}</p>
                {(c.email || c.jobTitle) && (
                  <p className="text-xs text-gray-500 truncate">{[c.jobTitle, c.email].filter(Boolean).join(' · ')}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
